/**
 * We forked this file from
 * https://github.com/clentfort/urql-custom-scalars-exchange, because that
 * exchange only supported serializing query responses, but we wanted to use
 * custom scalars in mutations as well.
 *
 * The substantial changes all stem from the exchange before `forward`, which
 * runs on mutation operations.
 */
// MIT License
//
// Copyright (c) 2020 Christian Lentfort
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { Exchange } from 'urql';
import type { AnyFunction } from '@watershed/shared-util/types/AnyFunction';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  ASTNode,
  buildClientSchema,
  GraphQLScalarType,
  GraphQLType,
  IntrospectionQuery,
  isInputObjectType,
  isListType,
  isNonNullType,
  isScalarType,
  Kind,
  TypeInfo,
  visit,
  visitWithTypeInfo,
} from 'graphql';
// eslint-disable-next-line import/no-extraneous-dependencies
import { DocumentNode, isNode } from 'graphql/language/ast';
import { map, pipe } from 'wonka';
// eslint-disable-next-line import/no-extraneous-dependencies
import produce from 'immer';
import optionalOnly from '@watershed/shared-universal/utils/optionalOnly';
import only from '@watershed/shared-universal/utils/only';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';
import { TypedDocumentNode } from 'urql';
import assertNever from '@watershed/shared-util/src/assertNever';

interface ScalarWithPath {
  /**
   * The name of the fragment if the field appeared inside of a fragment
   */
  /**
   * The name of the scalar
   */
  name: string;
  /**
   * The path to the scalar in the data returned from the server
   */
  path: Array<PropertyKey>;
}

interface ScalarInNode extends ScalarWithPath {
  kind: 'Scalar';
}

interface FragmentInNode {
  kind: 'FragmentSpread';
  fragmentName: string;
  /**
   * The path to the fragment spread in the data returned from the server
   */
  path: Array<PropertyKey>;
}

type NodeWithPath = ScalarInNode | FragmentInNode;

interface ScalarConfig
  extends Required<Pick<GraphQLScalarType, 'serialize' | 'parseValue'>> {}

interface AnyScalars {
  [key: string]: ScalarConfig | undefined;
}

interface ScalarExchangeOptions<Scalars extends AnyScalars> {
  scalars: Scalars;
  schema: IntrospectionQuery;
}

function traverseAncestors(
  astPath: ReadonlyArray<number | string>,
  ancestorAstNodes: ReadonlyArray<ASTNode | ReadonlyArray<ASTNode>>,
  callback: (node: ASTNode) => void
): void {
  let currentAstNode = ancestorAstNodes[0];
  astPath.forEach((segment) => {
    // @ts-expect-error
    currentAstNode = currentAstNode[segment];
    if (isNode(currentAstNode)) {
      callback(currentAstNode);
    }
  });
}

function getPathAndFragmentName(
  astPath: ReadonlyArray<number | string>,
  ancestorAstNodes: ReadonlyArray<ASTNode | ReadonlyArray<ASTNode>>
): [Array<PropertyKey>, string | undefined] {
  const path: Array<PropertyKey> = [];
  let fragmentName: string | undefined;
  traverseAncestors(astPath, ancestorAstNodes, (node) => {
    if (node.kind === Kind.FIELD) {
      if (node.alias) {
        path.push(node.alias.value);
      } else {
        path.push(node.name.value);
      }
    } else if (node.kind === Kind.FRAGMENT_DEFINITION) {
      fragmentName = node.name.value;
    }
  });

  return [path, fragmentName];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapScalar(data: any, path: Array<PropertyKey>, map: AnyFunction) {
  if (data == null) return data;

  const newData = { ...data };

  let newSubData = newData;
  for (let index = 0; index < path.length - 1; index += 1) {
    const segment = path[index];
    if (Array.isArray(newSubData[segment])) {
      const subPath = path.slice(index + 1);
      newSubData[segment] = newSubData[segment].map((subData: unknown) =>
        mapScalar(subData, subPath, map)
      );
      return newData;
    } else if (newSubData[segment] === null) {
      return newData;
    } else {
      newSubData[segment] = { ...newSubData[segment] };
    }
    newSubData = newSubData[segment];
  }

  const finalSegment = path[path.length - 1];

  if (Array.isArray(newSubData[finalSegment])) {
    newSubData[finalSegment] = newSubData[finalSegment].map(map);
  } else if (newSubData[finalSegment] != null) {
    newSubData[finalSegment] = map(newSubData[finalSegment]);
  }

  return newData;
}

function unpackType(
  type: GraphQLType | null,
  path: ReadonlyArray<string> = []
): Array<{ type: GraphQLScalarType; path: ReadonlyArray<string> }> {
  if (isNonNullType(type) || isListType(type)) {
    return unpackType(type.ofType, path);
  }
  if (isScalarType(type)) {
    return [{ type, path }];
  }
  if (isInputObjectType(type)) {
    return Object.entries(type.getFields()).flatMap(([fieldName, field]) => {
      return unpackType(field.type, [...path, fieldName]);
    });
  }
  return [];
}

function mapOrApply<T, R>(
  value: T | Array<T> | null | undefined,
  transform: (value: T) => R
): R | ReadonlyArray<R> | null | undefined {
  if (Array.isArray(value)) {
    return value.map(transform);
  }
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }
  return transform(value);
}

function updateScalarDeepInObject(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any,
  path: ReadonlyArray<PropertyKey>,
  transform: AnyFunction
) {
  if (path.length === 1) {
    const finalSegment = only(path);
    data[finalSegment] = mapOrApply(data[finalSegment], transform);
    return;
  }

  const [segment, ...rest] = path;
  mapOrApply(data[segment], (value) =>
    updateScalarDeepInObject(value, rest, transform)
  );
}

export default function scalarExchange<T extends AnyScalars>({
  schema,
  scalars,
}: ScalarExchangeOptions<T>): Exchange {
  const clientSchema = buildClientSchema(schema);
  const typeInfoInstance = new TypeInfo(clientSchema);
  const getScalarsInQuery = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    query: DocumentNode | TypedDocumentNode<any, Record<string, unknown>>
  ) => {
    const nodesInQuery: Array<NodeWithPath> = [];
    // Keyed by fragment name.
    const nodesInFragments: Partial<Record<string, Array<NodeWithPath>>> = {};

    const visitor = visitWithTypeInfo(typeInfoInstance, {
      Field(_node, _key, _parent, astPath, ancestorAstNodes) {
        const fieldType = typeInfoInstance.getType();
        if (fieldType == null) return;

        const scalarType = optionalOnly(unpackType(fieldType));
        if (scalarType == null) return;

        const { name } = scalarType.type;
        if (scalars[name] == null) return;

        const [path, fragmentName] = getPathAndFragmentName(
          astPath,
          ancestorAstNodes
        );

        const scalarInNode: ScalarInNode = { kind: 'Scalar', name, path };
        if (fragmentName == null) {
          nodesInQuery.push(scalarInNode);
        } else {
          nodesInFragments[fragmentName] = [
            nodesInFragments[fragmentName],
            scalarInNode,
          ]
            .filter(isNotNullish)
            .flat();
        }
      },
      FragmentSpread(node, _key, _parent, astPath, ancestorAstNodes) {
        const [path, fragmentName] = getPathAndFragmentName(
          astPath,
          ancestorAstNodes
        );

        const fragmentInNode: FragmentInNode = {
          kind: 'FragmentSpread',
          fragmentName: node.name.value,
          path,
        };
        if (fragmentName == null) {
          nodesInQuery.push(fragmentInNode);
        } else {
          nodesInFragments[fragmentName] = [
            nodesInFragments[fragmentName],
            fragmentInNode,
          ]
            .filter(isNotNullish)
            .flat();
        }
      },
    });

    visit(query, visitor);

    // Keyed by fragment name.
    const resolvedScalarsInFragments: Record<
      string,
      Array<ScalarWithPath>
    > = {};
    const resolveScalarsInFragment = (
      fragmentName: string,
      visitedFragmentNames: Array<string> = []
    ): Array<ScalarWithPath> => {
      if (resolvedScalarsInFragments[fragmentName]) {
        return resolvedScalarsInFragments[fragmentName];
      }

      if (visitedFragmentNames.includes(fragmentName)) {
        // There's a cycle in the nested fragments; we should do something here
        // but not error (because it's technically legal).
        return [];
      }

      const scalarsInFragment: Array<ScalarWithPath> = [];
      nodesInFragments[fragmentName]?.forEach((nodeWithPath) => {
        switch (nodeWithPath.kind) {
          case 'Scalar':
            scalarsInFragment.push(nodeWithPath);
            break;
          case 'FragmentSpread':
            const newScalars: Array<ScalarWithPath> = resolveScalarsInFragment(
              nodeWithPath.fragmentName,
              [...visitedFragmentNames, fragmentName]
            ).map((scalarWithPath) => ({
              ...scalarWithPath,
              path: [...nodeWithPath.path, ...scalarWithPath.path],
            }));
            scalarsInFragment.push(...newScalars);
            break;
          default:
            assertNever(nodeWithPath);
        }
      });
      resolvedScalarsInFragments[fragmentName] = scalarsInFragment;
      return scalarsInFragment;
    };

    const scalarsInQuery: Array<ScalarWithPath> = [];
    nodesInQuery.forEach((nodeWithPath) => {
      switch (nodeWithPath.kind) {
        case 'Scalar':
          scalarsInQuery.push(nodeWithPath);
          break;
        case 'FragmentSpread':
          const newScalars: Array<ScalarWithPath> = resolveScalarsInFragment(
            nodeWithPath.fragmentName
          ).map((scalarWithPath) => ({
            ...scalarWithPath,
            path: [...nodeWithPath.path, ...scalarWithPath.path],
          }));
          scalarsInQuery.push(...newScalars);
          break;
        default:
          assertNever(nodeWithPath);
      }
    });
    return scalarsInQuery;
  };

  const makeMutationVisitor = (nodesOfInterest: Array<ScalarWithPath>) =>
    visitWithTypeInfo(typeInfoInstance, {
      VariableDefinition(node) {
        const inputType = typeInfoInstance.getInputType();
        const updates = unpackType(inputType ?? null);
        nodesOfInterest.push(
          ...updates.map((update) => ({
            kind: 'Scalar' as const,
            name: update.type.name,
            path: [node.variable.name.value, ...update.path],
          }))
        );
      },
    });

  return ({ forward }) =>
    (operations$) => {
      return pipe(
        operations$,
        map((operation) => {
          // We have to update mutations before the request is sent.
          if (operation.kind !== 'mutation') return operation;

          const nodesOfInterest: Array<ScalarWithPath> = [];
          visit(operation.query, makeMutationVisitor(nodesOfInterest));

          const variables = produce(
            operation.variables,
            (draft: typeof operation.variables) => {
              if (operation.variables == null) return;
              for (const node of nodesOfInterest) {
                const customScalar = scalars[node.name];
                if (!customScalar) continue;
                updateScalarDeepInObject(
                  draft,
                  node.path,
                  customScalar.serialize
                );
              }
            }
          );

          return {
            ...operation,
            variables,
          };
        }),
        forward,
        map((args) => {
          // We have to update queries and mutation payloads after we receive
          // the response.
          if (args.data == null) {
            return args;
          }

          const scalarsInQuery = getScalarsInQuery(args.operation.query);
          if (scalarsInQuery.length === 0) {
            return args;
          }

          scalarsInQuery.forEach(({ name, path }) => {
            const parser = scalars[name]?.parseValue;
            if (!parser) return;
            args.data = mapScalar(args.data, path, parser);
          });
          return args;
        })
      );
    };
}
